<script setup>
import { computed, ref, watch, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import { loteFabricacaoSearch } from '@/search-definitions/loteFabricacaoSearch'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import DocumentoEntradaGrid from '@/components/Compras/documentoEntrada/DocumentoEntradaGrid.vue'
import { imprimirPagedJs } from '@/services/relatorioPrintService'
import { useConversaoQuantidade } from '@/composables/useConversaoQuantidade'

const props = defineProps({
  itensEmbarque: { type: Array, default: () => [] },
  packingListItems: { type: Array, default: () => [] },
  containerItems: { type: Array, default: () => [] },
  listas: { type: Object, default: () => ({}) },
  podeEditar: { type: Boolean, default: false },
  parceiros: { type: Object, default: () => ({}) },
  dadosEmbarque: { type: Object, default: () => ({}) }
})

const emit = defineEmits([
  'vincular-produto',
  'vincular-container',
  'desvincular-container',
  'desvincular-produto',
  'toggle-flag-lote',
  'show-notification'
])

// ============================================================
// MODAL DE RELATÓRIO DE DISTRIBUIÇÃO
// ============================================================
const showRelatorioModal = ref(false)
const relatorioContainerRef = ref(null)

// --- Empilhamento (z-index) via registry central ---
const tokenZRelatorio = ref(null)
const zRelatorio = ref(BASE_ZINDEX)

watch(showRelatorioModal, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZRelatorio.value = token
    zRelatorio.value = z
  } else {
    liberarZIndex(tokenZRelatorio.value)
    tokenZRelatorio.value = null
  }
})

// ============================================================
// ESTADO DO DRAG & DROP
// ============================================================
const draggingOverId = ref(null)
const collapsedGroups = ref({})

// ============================================================
// ESTADO PARA DOCUMENTOS DE ENTRADA (Lançar Despesas)
// ============================================================
const authStore = useAuthStore()
const showModalDocumentosGrid = ref(false)
const documentoSelecionado = ref(null)
const documentoEntradaGridRef = ref(null)
const containerEmFoco = ref(null) // idEmbarqueContainer do container selecionado

const toggleGroup = (id) => {
  collapsedGroups.value[id] = !collapsedGroups.value[id]
}

const onDragStart = (event, item, tipo) => {
  if (!props.podeEditar) {
    event.preventDefault()
    return
  }
  // Bloqueia drag de packing-list já alocado em container
  if (tipo === 'packing-list' && item.idEmbarqueContainer) {
    event.preventDefault()
    return
  }
  const data = {
    tipo,
    id: tipo === 'item-embarque' ? item.idEmbarqueItem : item.idEmbarquePackingList,
    idProduto: item.idProduto,
    nomeProduto: item.nomeProduto || item.descricaoProduto || '',
    sku: item.skuProduto || '',
    // Usar IdParceiroNegocioProdutor do DocumentoEntradaItem para filtro de lote de fabricação
    idParceiroNegocioProdutor: item.idParceiroNegocioProdutor || item.idFornecedorDocumento || null,
    lstUnidadeMedidaBasica: item.lstUnidadeMedidaBasica || '',
    lstUnidadeMedidaFornecedor: item.lstUnidadeMedidaFornecedor || '',
    fatorConversao: item.fatorConversao || null,
    // Para item-embarque: saldos disponíveis
    saldoDisponivelBase: item.saldoDisponivelBase || 0,
    saldoDisponivelForn: item.saldoDisponivelForn || 0,
    // Lote de fabricação
    idLoteFabricacao: item.idLoteFabricacao || null,
    numeroLoteFornecedor: item.numeroLoteFornecedor || '',
    controlaLote: !!item.controlaLote
  }
  event.dataTransfer.setData('application/json', JSON.stringify(data))
  event.dataTransfer.effectAllowed = 'move'
}

const onDragOver = (event, id) => {
  if (!props.podeEditar) return
  event.preventDefault()
  draggingOverId.value = id
  event.dataTransfer.dropEffect = 'move'
}

const onDragLeave = (event, id) => {
  if (draggingOverId.value === id) {
    draggingOverId.value = null
  }
}

// ============================================================
// CÁLCULO DE SALDOS (Produtos do Embarque - Alocado no PackingList)
// ============================================================
const totalAlocadoPorItem = computed(() => {
  const map = {}
  for (const pl of (props.packingListItems || [])) {
    for (const prod of (pl.produtos || [])) {
      const idItem = prod.idEmbarqueItem
      if (!map[idItem]) {
        map[idItem] = { base: 0, forn: 0 }
      }
      map[idItem].base += Number(prod.quantidadeEmbarcadaUnBasica) || 0
      map[idItem].forn += Number(prod.quantidadeEmbarcadaUnFornecedor) || 0
    }
  }
  return map
})

const itensComSaldo = computed(() => {
  return (props.itensEmbarque || []).map(item => {
    const alocado = totalAlocadoPorItem.value[item.idEmbarqueItem] || { base: 0, forn: 0 }
    const saldoBase = Math.max(0, (Number(item.quantidadeEmbarcadaUnBasica) || 0) - alocado.base)
    const saldoForn = Math.max(0, (Number(item.quantidadeEmbarcadaUnFornecedor) || 0) - alocado.forn)
    return {
      ...item,
      saldoDisponivelBase: parseFloat(saldoBase.toFixed(4)),
      saldoDisponivelForn: parseFloat(saldoForn.toFixed(4))
    }
  }).filter(item => item.saldoDisponivelBase > 0 || item.saldoDisponivelForn > 0)
})

// ============================================================
// CÁLCULO DE SALDOS DE PESO E VOLUME POR PACKING LIST
// ============================================================
const alocacaoPesoVolumePorLote = computed(() => {
  const map = {}
  for (const pl of (props.packingListItems || [])) {
    const id = pl.idEmbarquePackingList
    let alocadoPesoLiq = 0
    let alocadoPesoBru = 0
    let alocadoVolCbm = 0
    for (const prod of (pl.produtos || [])) {
      alocadoPesoLiq += Number(prod.pesoLiquidoKg) || 0
      alocadoPesoBru += Number(prod.pesoBrutoKg) || 0
      alocadoVolCbm += Number(prod.volumeCBM) || 0
    }
    map[id] = {
      totalPesoLiquido: Number(pl.pesoLiquidoTotalKg) || 0,
      totalPesoBruto: Number(pl.pesoBrutoTotalKg) || 0,
      totalVolumeCBM: Number(pl.volumeCBMTotal) || 0,
      alocadoPesoLiquido: alocadoPesoLiq,
      alocadoPesoBruto: alocadoPesoBru,
      alocadoVolumeCBM: alocadoVolCbm,
      saldoPesoLiquido: Math.max(0, (Number(pl.pesoLiquidoTotalKg) || 0) - alocadoPesoLiq),
      saldoPesoBruto: Math.max(0, (Number(pl.pesoBrutoTotalKg) || 0) - alocadoPesoBru),
      saldoVolumeCBM: Math.max(0, (Number(pl.volumeCBMTotal) || 0) - alocadoVolCbm)
    }
  }
  return map
})

const itensAgrupados = computed(() => {
  const grupos = {}
  for (const item of (itensComSaldo.value || [])) {
    // Chave única: numeroDocumentoEntrada (agrupa todos itens do mesmo documento)
    const docKey = item.numeroDocumentoEntrada || 'sem-doc'
    if (!grupos[docKey]) {
      // Busca nome do fornecedor: prioriza nomeFornecedor vindo da API, depois parceiros, depois fallback
      const nomeFornecedor = item.nomeFornecedor ||
        (item.idFornecedorDocumento ? (props.parceiros[item.idFornecedorDocumento] || '') : '') ||
        'Sem Fornecedor'

      const tipoDoc = item.tipoDocumentoEntradaNome || 'Doc'
      const numDoc = item.numeroDocumentoEntrada || ''

      const label = item.numeroDocumentoEntrada
        ? `${tipoDoc} ${numDoc} - ${nomeFornecedor}`
        : 'Itens Sem Documento'

      grupos[docKey] = {
        key: docKey,
        label,
        itens: []
      }
    }
    grupos[docKey].itens.push(item)
  }
  return Object.values(grupos)
})

// ============================================================
// MODAL DE FRACIONAMENTO
// ============================================================
const showFractionModal = ref(false)

// --- Empilhamento (z-index) via registry central ---
const tokenZFraction = ref(null)
const zFraction = ref(BASE_ZINDEX)

watch(showFractionModal, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZFraction.value = token
    zFraction.value = z
  } else {
    liberarZIndex(tokenZFraction.value)
    tokenZFraction.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZRelatorio.value)
  liberarZIndex(tokenZFraction.value)
  tokenZRelatorio.value = null
  tokenZFraction.value = null
})

const fractionModalData = ref({
  idPackingList: null,
  idEmbarqueItem: null,
  idProduto: null,
  nomeProduto: '',
  sku: '',
  idParceiroNegocioProdutor: null,
  lstUnidadeMedidaBasica: '',
  lstUnidadeMedidaFornecedor: '',
  fatorConversao: null,
  saldoDisponivelBase: 0,
  saldoDisponivelForn: 0,
  packingListDescricao: '',
  quantidadeBase: 0,
  quantidadeForn: 0,
  // Pesos e Volume CBM do produto
  pesoLiquidoKg: 0,
  pesoBrutoKg: 0,
  volumeCBM: null,
  // Saldos do lote destino (para limitar sliders)
  saldoPesoLiquido: 0,
  saldoPesoBruto: 0,
  saldoVolumeCBM: 0,
  // Lote de fabricação
  controlaLote: false,
  idLoteFabricacao: null,
  numeroLoteFornecedor: ''
})

// ============================================================
// VERIFICAÇÃO SE PACKING LIST TEM PESO FÍSICO
// ============================================================
const packingListTemPesoFisico = computed(() => {
  const packingList = props.packingListItems.find(pl => pl.idEmbarquePackingList === fractionModalData.value.idPackingList)
  if (!packingList) return false
  return (Number(packingList.pesoBrutoTotalKg) || 0) > 0 || (Number(packingList.pesoLiquidoTotalKg) || 0) > 0
})

// ============================================================
// MENSAGEM DE ALERTA DINÂMICA SOBRE PESO
// ============================================================
const mensagemAlertaPeso = computed(() => {
  if (packingListTemPesoFisico.value) {
    return {
      tipo: 'warning',
      icone: 'bi-exclamation-triangle-fill',
      texto: 'O peso e volume adotados virão do rateio dos totais do packing list. Este peso é o físico. O peso documental se mantém no cadastro dos produtos do embarque.'
    }
  } else {
    const item = props.itensEmbarque.find(i => i.idEmbarqueItem === fractionModalData.value.idEmbarqueItem)
    const pesoDoc = item ? (Number(item.pesoBrutoDocumentalKg) || 0) : 0
    return {
      tipo: 'info',
      icone: 'bi-info-circle-fill',
      texto: `Peso documental adotado como peso físico: ${formatarNumero(pesoDoc, 3)} kg. O volume adotado virá do rateio do total do packing list.`
    }
  }
})

// ============================================================
// CÁLCULO DE RATEIO DE PESOS
// ============================================================
const calcularRateioPesos = (packingListId, novaQuantidadeBase) => {
  const packingList = props.packingListItems.find(pl => pl.idEmbarquePackingList === packingListId)
  if (!packingList) return null

  const temPesoFisico = (Number(packingList.pesoBrutoTotalKg) || 0) > 0 || (Number(packingList.pesoLiquidoTotalKg) || 0) > 0

  // Se não tem peso físico, retorna peso documental do item
  if (!temPesoFisico) {
    const item = props.itensEmbarque.find(i => i.idEmbarqueItem === fractionModalData.value.idEmbarqueItem)
    return {
      pesoBrutoKg: Number(item?.pesoBrutoDocumentalKg) || 0,
      pesoLiquidoKg: Number(item?.pesoLiquidoDocumentalKg) || 0,
      volumeCBM: null
    }
  }

  // Calcula quantidade total (existente + nova)
  const quantidadeExistente = (packingList.produtos || []).reduce(
    (sum, p) => sum + (Number(p.quantidadeEmbarcadaUnBasica) || 0), 0
  )
  const quantidadeTotal = quantidadeExistente + novaQuantidadeBase

  if (quantidadeTotal === 0) return null

  // Calcula proporção
  const proporcao = novaQuantidadeBase / quantidadeTotal

  return {
    pesoBrutoKg: parseFloat(((Number(packingList.pesoBrutoTotalKg) || 0) * proporcao).toFixed(3)),
    pesoLiquidoKg: parseFloat(((Number(packingList.pesoLiquidoTotalKg) || 0) * proporcao).toFixed(3)),
    volumeCBM: packingList.volumeCBMTotal
      ? parseFloat(((Number(packingList.volumeCBMTotal) || 0) * proporcao).toFixed(4))
      : null
  }
}

// Configuração de busca de lote para o modal — filtra pelo fornecedor selecionado
const configBuscaLoteFabricacaoModal = computed(() => {
  const idFornecedor = fractionModalData.value.idParceiroNegocioProdutor
  if (!idFornecedor) {
    return {
      ...loteFabricacaoSearch,
      titulo: 'Selecione um fornecedor primeiro',
      filtrosFixos: { idFornecedor: -1 }
    }
  }
  return {
    ...loteFabricacaoSearch,
    titulo: 'Buscar Lote de Fabricação',
    filtrosFixos: { idFornecedor }
  }
})

// ============================================================
// AJUSTE DE QUANTIDADES NO MODAL DE FRACIONAMENTO
// ============================================================
/**
 * Ajusta a quantidade de um campo do modal de fracionamento.
 * @param {string} campo - 'quantidadeForn' | 'quantidadeBase'
 * @param {number} delta - valor a incrementar (pode ser 1, 0.1, 0.01, -1, -0.1, -0.01)
 */
const ajustarQuantidade = (campo, delta) => {
  const saldo = campo === 'quantidadeForn'
    ? fractionModalData.value.saldoDisponivelForn
    : fractionModalData.value.saldoDisponivelBase

  const atual = Number(fractionModalData.value[campo]) || 0
  const novo = Math.max(0, Math.min(saldo, atual + delta))
  fractionModalData.value[campo] = parseFloat(novo.toFixed(4))
}

const onDropOnPackingList = (event, packingList) => {
  draggingOverId.value = null
  if (!props.podeEditar) return

  const rawData = event.dataTransfer.getData('application/json')
  if (!rawData) return

  try {
    const data = JSON.parse(rawData)
    if (data.tipo !== 'item-embarque') return

    // Bloqueia associação se o packing list já está com preenchimento concluído
    if (packingList.flagPreenchimentoConcluido) {
      emit('show-notification', {
        message: 'Este lote está marcado como preenchimento concluído. Desmarque o flag para adicionar novos itens.',
        type: 'warning'
      })
      return
    }

    // Verifica se o packing list já tem produtos com idProduto diferente
    const produtosExistentes = packingList.produtos || []
    if (produtosExistentes.length > 0) {
      const idProdutoExistente = produtosExistentes[0].idProduto
      if (idProdutoExistente !== data.idProduto) {
        emit('show-notification', {
          message: 'Este packing list já contém produtos diferentes. Apenas o mesmo produto é permitido.',
          type: 'error'
        })
        return
      }
    }

    // Abre o modal com os dados do item arrastado
    const aloc = alocacaoPesoVolumePorLote.value[packingList.idEmbarquePackingList] || {}

    fractionModalData.value = {
      idPackingList: packingList.idEmbarquePackingList,
      idEmbarqueItem: data.id,
      idProduto: data.idProduto,
      nomeProduto: data.nomeProduto,
      sku: data.sku,
      idParceiroNegocioProdutor: data.idParceiroNegocioProdutor || null,
      lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
      lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
      fatorConversao: data.fatorConversao,
      saldoDisponivelBase: data.saldoDisponivelBase,
      saldoDisponivelForn: data.saldoDisponivelForn,
      packingListDescricao: packingList.descricao || `Lote #${packingList.idEmbarquePackingList}`,
      quantidadeBase: data.saldoDisponivelBase,
      quantidadeForn: data.saldoDisponivelForn,
      // Pesos e Volume
      pesoLiquidoKg: 0,
      pesoBrutoKg: 0,
      volumeCBM: null,
      saldoPesoLiquido: aloc.saldoPesoLiquido || 0,
      saldoPesoBruto: aloc.saldoPesoBruto || 0,
      saldoVolumeCBM: aloc.saldoVolumeCBM || 0,
      // Lote de fabricação
      controlaLote: !!data.controlaLote,
      idLoteFabricacao: data.idLoteFabricacao || null,
      numeroLoteFornecedor: data.numeroLoteFornecedor || ''
    }
    showFractionModal.value = true
  } catch (e) {
    console.error('Erro ao processar drop:', e)
  }
}

const { simular } = useConversaoQuantidade()

// Conversão centralizada (backend = fonte da verdade).
// Chamada no lost focus do campo de quantidade ou no botão "Calcular".
const calcularConversao = async (campo) => {
  const data = fractionModalData.value
  if (!data || !data.idProduto) return

  const saldoForn = Number(data.saldoDisponivelForn) || 0
  const saldoBase = Number(data.saldoDisponivelBase) || 0
  if (Number(data.quantidadeForn) > saldoForn) data.quantidadeForn = saldoForn
  if (Number(data.quantidadeBase) > saldoBase) data.quantidadeBase = saldoBase

  try {
    const resultado = await simular({
      idProduto: data.idProduto,
      quantidadeFornecedor: Number(data.quantidadeForn) || 0,
      quantidadeBase: Number(data.quantidadeBase) || 0,
      fatorConversao: Number(data.fatorConversao) || null,
      campoOrigem: campo === 'base' ? 'Base' : 'Fornecedor'
    })
    data.quantidadeForn = resultado.quantidadeFornecedor
    data.quantidadeBase = resultado.quantidadeBase
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao calcular a conversão.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const confirmarAlocacao = () => {
  const data = fractionModalData.value
  if (data.quantidadeBase <= 0 && data.quantidadeForn <= 0) {
    emit('show-notification', { message: 'Informe pelo menos uma quantidade maior que zero.', type: 'warning' })
    return
  }

  // Calcular pesos via rateio automático
  const pesosRateados = calcularRateioPesos(data.idPackingList, data.quantidadeBase)

  emit('vincular-produto', {
    idPackingList: data.idPackingList,
    produto: {
      idEmbarqueItem: data.idEmbarqueItem,
      idProduto: data.idProduto,
      quantidadeEmbarcadaUnBasica: data.quantidadeBase,
      quantidadeEmbarcadaUnFornecedor: data.quantidadeForn,
      lstUnidadeMedidaBasica: data.lstUnidadeMedidaBasica,
      lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor,
      fatorConversao: data.fatorConversao,
      // Usar pesos rateados automaticamente
      pesoLiquidoKg: pesosRateados?.pesoLiquidoKg || 0,
      pesoBrutoKg: pesosRateados?.pesoBrutoKg || 0,
      volumeCBM: pesosRateados?.volumeCBM || null,
      idLoteFabricacao: data.idLoteFabricacao || null
    }
  })

  showFractionModal.value = false
}

const fecharModal = () => {
  showFractionModal.value = false
}

// ============================================================
// DROP NO CONTAINER
// ============================================================
const onDropOnContainer = (event, container) => {
  draggingOverId.value = null
  if (!props.podeEditar) return

  const rawData = event.dataTransfer.getData('application/json')
  if (!rawData) return

  try {
    const data = JSON.parse(rawData)
    if (data.tipo !== 'packing-list') return

    const packingList = (props.packingListItems || []).find(pl => pl.idEmbarquePackingList === data.id)
    if (!packingList) return

    if (!packingList.flagPreenchimentoConcluido) {
      emit('show-notification', {
        message: 'Este lote NÃO está marcado como preenchido. Primeiramente vincule produtos e marque para permitir estufagem.',
        type: 'warning'
      })
      return
    }

    emit('vincular-container', {
      idPackingList: data.id,
      idContainer: container.idEmbarqueContainer
    })
  } catch (e) {
    console.error('Erro ao processar drop no container:', e)
  }
}

// ============================================================
// HELPERS
// ============================================================
const getTextoLista = (lista, id) => {
  if (!lista) return id || '-'
  const found = lista.find(l => l.idListaClienteItem === id)
  return found ? found.textoLista : (id || '-')
}

const formatarNumero = (val, decimals = 4) => {
  if (val === null || val === undefined || val === '') return '0'
  const num = Number(val)
  if (isNaN(num)) return '0'
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  })
}

const packingListNoContainer = (pl) => {
  return !pl.idEmbarqueContainer
}

const onDesvincularProduto = (idPackingList, idProdutoPackingList, packingList) => {
  if (!props.podeEditar) return
  emit('desvincular-produto', {
    idPackingList,
    idEmbarquePackingListProduto: idProdutoPackingList
  })
  // Se o packing list estava com flag de preenchimento concluído, resetar para false
  if (packingList.flagPreenchimentoConcluido) {
    emit('toggle-flag-lote', {
      idPackingList: packingList.idEmbarquePackingList,
      flagPreenchimentoConcluido: false
    })
  }
}

const onDesvincularContainer = (packingList) => {
  if (!props.podeEditar) return
  // Se o packing list estava com flag de preenchimento concluído, resetar para false
  if (packingList.flagPreenchimentoConcluido) {
    emit('toggle-flag-lote', {
      idPackingList: packingList.idEmbarquePackingList,
      flagPreenchimentoConcluido: false
    })
  }
  emit('desvincular-container', packingList.idEmbarquePackingList)
}

const onToggleFlagLote = (packingList) => {
  if (!props.podeEditar) return
  emit('toggle-flag-lote', {
    idPackingList: packingList.idEmbarquePackingList,
    flagPreenchimentoConcluido: !packingList.flagPreenchimentoConcluido
  })
}

const alocacoesDoContainer = (containerId) => {
  return (props.packingListItems || []).filter(pl => pl.idEmbarqueContainer === containerId)
}

// ============================================================
// ORDENAÇÃO E CONTROLE DA COLUNA LOTES A ESTUFAR
// ============================================================

// Ordenar packing lists: não alocados primeiro, alocados por último
const packingListOrdenados = computed(() => {
  return (props.packingListItems || []).slice().sort((a, b) => {
    const aAlocado = !!a.idEmbarqueContainer
    const bAlocado = !!b.idEmbarqueContainer
    return aAlocado === bAlocado ? 0 : aAlocado ? 1 : -1
  })
})

// Verificar se todos os packing lists estão alocados em containers
const todosPackingListsAlocados = computed(() => {
  if (!(props.packingListItems || []).length) return false
  return props.packingListItems.every(pl => !!pl.idEmbarqueContainer)
})

// Ordenar containers: vazios (sem packing list) no topo
const containerItemsOrdenados = computed(() => {
  return (props.containerItems || []).slice().sort((a, b) => {
    const aVazio = (alocacoesDoContainer(a.idEmbarqueContainer) || []).length === 0
    const bVazio = (alocacoesDoContainer(b.idEmbarqueContainer) || []).length === 0
    if (aVazio === bVazio) return 0
    return aVazio ? -1 : 1
  })
})

const pesoBrutoTotalContainer = (containerId) => {
  return alocacoesDoContainer(containerId).reduce((acc, pl) => acc + (Number(pl.pesoBrutoTotalKg) || 0), 0)
}

const getContainerNumero = (idContainer) => {
  const c = (props.containerItems || []).find(c => c.idEmbarqueContainer === idContainer)
  return c ? c.numeroContainer : 'S/ Nº'
}

// ============================================================
// FUNÇÕES DO RELATÓRIO
// ============================================================
const abrirRelatorioDistribuicao = () => {
  showRelatorioModal.value = true
}

const gerarPDF = async () => {
  if (!relatorioContainerRef.value) return

  try {
    await imprimirPagedJs(relatorioContainerRef.value, {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      arquivo: `relatorio-estufagem-${new Date().toISOString().split('T')[0]}`
    })
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    emit('show-notification', { message: 'Erro ao gerar o PDF do relatório.', type: 'error' })
  }
}

const calcularPesoBrutoTotal = () => {
  return formatarNumero(
    (props.packingListItems || []).reduce((acc, pl) => acc + (Number(pl.pesoBrutoTotalKg) || 0), 0),
    2
  )
}

const calcularVolumeTotal = () => {
  return formatarNumero(
    (props.packingListItems || []).reduce((acc, pl) => acc + (Number(pl.volumeCBMTotal) || 0), 0),
    4
  )
}

const somarQtdForn = (produtos) => {
  return (produtos || []).reduce((acc, p) => acc + (Number(p.quantidadeEmbarcadaUnFornecedor) || 0), 0)
}

const somarQtdBase = (produtos) => {
  return (produtos || []).reduce((acc, p) => acc + (Number(p.quantidadeEmbarcadaUnBasica) || 0), 0)
}

const somarPesoLiquido = (produtos) => {
  return (produtos || []).reduce((acc, p) => acc + (Number(p.pesoLiquidoKg) || 0), 0)
}

const somarPesoBruto = (produtos) => {
  return (produtos || []).reduce((acc, p) => acc + (Number(p.pesoBrutoKg) || 0), 0)
}

const somarVolume = (produtos) => {
  return (produtos || []).reduce((acc, p) => acc + (Number(p.volumeCBM) || 0), 0)
}

const packingListNaoAlocados = computed(() => {
  return (props.packingListItems || []).filter(pl => !pl.idEmbarqueContainer)
})

// ============================================================
// LANÇAR DESPESAS POR CONTAINER
// ============================================================
const abrirLancarDespesas = (container) => {
  containerEmFoco.value = container.idEmbarqueContainer
  showModalDocumentosGrid.value = true
}

// ============================================================
// MODAL DE VISUALIZAÇÃO DO CONTAINER
// ============================================================
const showContainerViewerModal = ref(false)
const containerViewerData = ref(null)

const abrirContainerViewer = (container) => {
  containerViewerData.value = container
  showContainerViewerModal.value = true
}

const volumesPorContainer = (containerId) => {
  const packingLists = alocacoesDoContainer(containerId)
  const volumes = {}
  for (const pl of packingLists) {
    const qtd = Number(pl.quantidadeVolumes) || 0
    if (qtd > 0) {
      const tipo = pl.lstTipoVolume
      const tipoTexto = getTextoLista(props.listas.LstTipoVolume, tipo)
      if (!volumes[tipo]) {
        volumes[tipo] = { quantidade: 0, tipoTexto }
      }
      volumes[tipo].quantidade += qtd
    }
  }
  return Object.values(volumes)
}

// Expor método para o componente pai
defineExpose({
  abrirRelatorioDistribuicao
})
</script>

<template>
  <div class="smart-mode-grid">
    <!-- ============================================================ -->
    <!-- COLUNA ESQUERDA: PRODUTOS DO EMBARQUE -->
    <!-- ============================================================ -->
    <div class="smart-column column-items">
      <div class="column-header">
        <h5 class="mb-0 d-flex align-items-center gap-2">
          <i class="bi bi-box-seam"></i>
          Produtos do Embarque
          <span v-if="(itensComSaldo || []).length > 0" class="badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle ms-1">
            {{ (itensComSaldo || []).length }}
          </span>
        </h5>
        <small class="text-muted">Arraste para alocar no Packing List</small>
      </div>
      <div class="column-body">
        <template v-if="(itensComSaldo || []).length === 0">
          <div class="empty-state">
            <i class="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Nenhum item pendente de alocação.</p>
          </div>
        </template>
        <template v-else>
          <div v-for="grupo in itensAgrupados" :key="grupo.key" class="grupo-documento mb-3">
            <div class="grupo-documento-label" @click="toggleGroup(grupo.key)">
              <i class="bi" :class="collapsedGroups[grupo.key] ? 'bi-chevron-right' : 'bi-chevron-down'"></i>
              {{ grupo.label }}
            </div>

            <div v-if="!collapsedGroups[grupo.key]" class="grupo-documento-body">
              <div
                v-for="item in grupo.itens"
                :key="item.idEmbarqueItem"
                draggable="true"
                @dragstart="onDragStart($event, item, 'item-embarque')"
                class="draggable-card card-item-embarque"
              >
                <!-- 1. SKU -->
                <div class="card-sku" v-if="item.skuProduto"><code>{{ item.skuProduto }}</code></div>

                <!-- 2. Nome do Produto -->
                <div class="card-produto-nome">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</div>

                <hr class="my-2" />

                <!-- 3. Saldos -->
                <div class="card-saldo">
                  <div class="saldo-row">
                    <span class="saldo-label">Saldo (Forn):</span>
                    <span class="saldo-value">{{ formatarNumero(item.saldoDisponivelForn) }}</span>
                  </div>
                  <div class="saldo-row">
                    <span class="saldo-label">Saldo (Base):</span>
                    <span class="saldo-value">{{ formatarNumero(item.saldoDisponivelBase) }}</span>
                  </div>
                </div>

                <hr class="my-2" />

                <!-- 4. Pesos Documentais -->
                <div class="card-saldo">
                  <div class="saldo-row">
                    <span class="saldo-label">Peso Bruto (Doc):</span>
                    <span class="saldo-value">{{ formatarNumero(item.pesoBrutoDocumentalKg, 3) }} kg</span>
                  </div>
                  <div class="saldo-row">
                    <span class="saldo-label">Peso Líq. (Doc):</span>
                    <span class="saldo-value">{{ formatarNumero(item.pesoLiquidoDocumentalKg, 3) }} kg</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- COLUNA CENTRAL: LOTES A ESTUFAR -->
    <!-- ============================================================ -->
    <div class="smart-column column-packing">
      <div class="column-header d-flex justify-content-between align-items-center"
           :class="todosPackingListsAlocados ? 'column-header-packing-completo' : 'column-header-packing'">
        <div>
          <h5 class="mb-0 d-flex align-items-center gap-2">
            <i class="bi bi-list-check"></i>
            Lotes a Estufar
            <span v-if="(packingListItems || []).length > 0" class="badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle ms-1">
              {{ (packingListItems || []).length }}
            </span>
          </h5>
          <small class="text-muted">Solte itens aqui ou arraste para containers</small>
        </div>
      </div>
      <div class="column-body">
        <template v-if="(packingListItems || []).length === 0">
          <div class="empty-state">
            <i class="bi bi-file-earmark-text fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Nenhum lote cadastrado.</p>
          </div>
        </template>
        <template v-else>
          <div
            v-for="lote in packingListOrdenados"
            :key="lote.idEmbarquePackingList"
            :draggable="packingListNoContainer(lote)"
            @dragstart="onDragStart($event, lote, 'packing-list')"
            @dragover.prevent="onDragOver($event, lote.idEmbarquePackingList)"
            @dragleave="onDragLeave($event, lote.idEmbarquePackingList)"
            @drop="onDropOnPackingList($event, lote)"
            class="dropzone-card card-lote"
            :class="{
              'drag-over': draggingOverId === lote.idEmbarquePackingList,
              'card-lote-pendente': packingListNoContainer(lote),
              'card-lote-alocado': !packingListNoContainer(lote)
            }"
            :title="!packingListNoContainer(lote) ? 'Lote alocado em container - remova do container para mover' : 'Arraste para um container'"
          >
            <!-- Badge de pendência de container -->
            <div v-if="packingListNoContainer(lote)" class="lote-badge-pendente">
              <i class="bi bi-exclamation-triangle-fill me-1"></i> Pendente de Container
            </div>
            <div v-else class="lote-badge-alocado">
              <i class="bi bi-check-circle-fill me-1"></i> Alocado no Container: {{ getContainerNumero(lote.idEmbarqueContainer) }}
            </div>

            <!-- Área de ação: Flag Preenchido (quando não alocado) ou Desalocar (quando alocado) -->
            <div class="lote-acao-principal">
              <!-- FlagPreenchimentoConcluido: checkbox visível apenas quando não está em container -->
              <div v-if="packingListNoContainer(lote)" class="lote-flag-checkbox" :class="{ 'disabled': !podeEditar }">
                <div class="form-check form-switch form-check-sm mb-0">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    :id="'flag-lote-' + lote.idEmbarquePackingList"
                    :checked="lote.flagPreenchimentoConcluido"
                    :disabled="!podeEditar"
                    @click.stop
                    @change.stop="onToggleFlagLote(lote)"
                  />
                  <label class="form-check-label" :for="'flag-lote-' + lote.idEmbarquePackingList">
                    <span v-if="lote.flagPreenchimentoConcluido" class="flag-text flag-ok">
                      <i class="bi bi-check-circle-fill me-1"></i>Preenchido
                    </span>
                    <span v-else class="flag-text flag-pendente">
                      <i class="bi bi-exclamation-circle me-1"></i>ASSOCIE OS PRODUTOS E MARQUE PARA PERMITIR ESTUFAGEM
                    </span>
                  </label>
                </div>
              </div>

              <!-- Botão desalocar do container (quando alocado) -->
              <button
                v-else-if="podeEditar"
                type="button"
                class="btn btn-sm btn-outline-danger desalocar-btn"
                title="Desalocar lote deste container"
                @click.stop="onDesvincularContainer(lote)"
              >
                <i class="bi bi-box-arrow-left me-1"></i>
                Desalocar do Container
              </button>
            </div>

            <!-- Título: se AiGenerated, destaca Descricao -->
            <div class="card-title" :class="{ 'card-title-ai': lote.aiGenerated }">
              {{ lote.descricao || `Lote #${lote.idEmbarquePackingList}` }}
            </div>

            <!-- Subtítulo: SKU e Produto (do primeiro produto) -->
            <div v-if="lote.nomeProduto || lote.sku" class="card-subtitle">
              <code v-if="lote.sku">{{ lote.sku }}</code>
              <span v-if="lote.nomeProduto"> - {{ lote.nomeProduto }}</span>
            </div>

            <!-- Info do lote -->
            <div class="lote-info">
              <div class="info-row">
                <span class="info-label">Volumes:</span>
                <span class="info-value">{{ formatarNumero(lote.quantidadeVolumes, 0) }} {{ getTextoLista(listas.LstTipoVolume, lote.lstTipoVolume) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Peso Bruto:</span>
                <span class="info-value">{{ formatarNumero(lote.pesoBrutoTotalKg) }} kg</span>
              </div>
              <div class="info-row" :class="{'text-warning-emphasis fw-bold': (alocacaoPesoVolumePorLote[lote.idEmbarquePackingList]?.saldoPesoLiquido || 0) > 0}">
                <span class="info-label">Peso Líquido:</span>
                <span class="info-value">
                  {{ formatarNumero(lote.pesoLiquidoTotalKg) }} kg
                  <i v-if="(alocacaoPesoVolumePorLote[lote.idEmbarquePackingList]?.saldoPesoLiquido || 0) > 0"
                     class="bi bi-exclamation-triangle-fill text-warning ms-1"
                     title="Pendente de alocação nos produtos"></i>
                </span>
              </div>
              <div class="info-row" :class="{'text-warning-emphasis fw-bold': (alocacaoPesoVolumePorLote[lote.idEmbarquePackingList]?.saldoVolumeCBM || 0) > 0}">
                <span class="info-label">Volume CBM:</span>
                <span class="info-value">
                  {{ formatarNumero(lote.volumeCBMTotal) }} m³
                  <i v-if="(alocacaoPesoVolumePorLote[lote.idEmbarquePackingList]?.saldoVolumeCBM || 0) > 0"
                     class="bi bi-exclamation-triangle-fill text-warning ms-1"
                     title="Pendente de alocação nos produtos"></i>
                </span>
              </div>
            </div>

            <!-- Produtos vinculados ao lote -->
            <div v-if="lote.produtos && lote.produtos.length > 0" class="lote-produtos">
              <div class="produtos-header">
                <span class="info-label">Produtos do Embarque</span>
              </div>
              <div
                v-for="prod in lote.produtos"
                :key="prod.idEmbarquePackingListProduto"
                class="produto-item"
              >
                <div class="produto-info">
                  <span class="produto-tag-item">
                    <span class="tag-label">Forn:</span>
                    <span class="tag-value">{{ formatarNumero(prod.quantidadeEmbarcadaUnFornecedor) }}</span>
                    <span class="tag-unidade">{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, prod.lstUnidadeMedidaFornecedor) }}</span>
                  </span>
                  <span class="tag-separator">|</span>
                  <span class="produto-tag-item">
                    <span class="tag-label">Base:</span>
                    <span class="tag-value">  {{ formatarNumero(prod.quantidadeEmbarcadaUnBasica) }}</span>
                    <span class="tag-unidade">{{ getTextoLista(listas.LstUnidadeMedidaBasica, prod.lstUnidadeMedidaBasica) }}</span>
                  </span>
                  <span class="tag-separator" v-if="prod.pesoBrutoKg || prod.pesoLiquidoKg">|</span>
                  <span class="produto-tag-item" v-if="prod.pesoBrutoKg || prod.pesoLiquidoKg">
                    <span class="tag-label">Pesos (kg): </span>
                    <span class="tag-value">{{ formatarNumero(prod.pesoBrutoKg, 2) }} / {{ formatarNumero(prod.pesoLiquidoKg, 2) }}</span>
                    <!-- <span class="tag-unidade">kg</span> -->
                  </span>
                  <span class="tag-separator" v-if="prod.volumeCBM">|</span>
                  <span class="produto-tag-item" v-if="prod.volumeCBM">
                    <span class="tag-label">Vol:</span>
                    <span class="tag-value">{{ formatarNumero(prod.volumeCBM, 3) }}</span>
                    <span class="tag-unidade">m³</span>
                  </span>
                </div>
                <button
                  v-if="podeEditar && packingListNoContainer(lote)"
                  type="button"
                  class="btn btn-sm btn-outline-danger desalocar-produto-btn"
                  title="Desalocar produto deste lote"
                  @click.stop="onDesvincularProduto(lote.idEmbarquePackingList, prod.idEmbarquePackingListProduto, lote)"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <!-- Flag IA -->
            <div v-if="lote.aiGenerated" class="lote-flag-ia">
              <i class="bi bi-magic me-1"></i> IA
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- COLUNA DIREITA: PLANO DE ESTUFAGEM -->
    <!-- ============================================================ -->
    <div class="smart-column column-containers">
      <div class="column-header column-header-containers">
        <h5 class="mb-0 d-flex align-items-center gap-2">
          <i class="bi bi-boxes"></i>
          Plano de Estufagem
          <span v-if="(containerItems || []).length > 0" class="badge bg-warning bg-opacity-10 text-warning-emphasis border border-warning-subtle ms-1">
            {{ (containerItems || []).length }}
          </span>
        </h5>
        <small class="text-muted">Solte lotes aqui para estufar</small>
      </div>
      <div class="column-body">
        <template v-if="(containerItems || []).length === 0">
          <div class="empty-state">
            <i class="bi bi-box-seam fs-1 d-block mb-2 text-muted"></i>
            <p class="mb-0">Nenhum container cadastrado.</p>
          </div>
        </template>
        <template v-else>
          <div
            v-for="cont in containerItemsOrdenados"
            :key="cont.idEmbarqueContainer"
            @dragover.prevent="onDragOver($event, cont.idEmbarqueContainer)"
            @dragleave="onDragLeave($event, cont.idEmbarqueContainer)"
            @drop="onDropOnContainer($event, cont)"
            class="dropzone-card card-container"
            :class="{ 'drag-over': draggingOverId === cont.idEmbarqueContainer }"
          >
            <!-- Header do Container -->
            <div class="container-header">
              <div class="container-numero">
                <i class="bi bi-box-seam me-1"></i>
                {{ cont.numeroContainer || 'S/ NÚMERO' }}
              </div>
              <div class="container-header-right">
                <div v-if="cont.aiGenerated" class="lote-flag-ia">
                  <i class="bi bi-magic me-1"></i> IA
                </div>
                <div class="container-tipo" v-if="cont.lstTipoContainer">
                  {{ getTextoLista(listas.LstTipoContainer, cont.lstTipoContainer) }}
                </div>
                <!-- Badge de Volumes -->
                <div v-if="volumesPorContainer(cont.idEmbarqueContainer).length > 0" class="container-volumes-badge">
                  <template v-for="(vol, idx) in volumesPorContainer(cont.idEmbarqueContainer)" :key="idx">
                    <i class="bi bi-box me-1"></i>
                    {{ vol.quantidade }} {{ vol.tipoTexto }}
                    <span v-if="idx < volumesPorContainer(cont.idEmbarqueContainer).length - 1" class="volumes-sep">|</span>
                  </template>
                </div>
                <button
                  v-if="podeEditar"
                  type="button"
                  class="btn btn-sm btn-outline-warning fw-bold btn-lancar-despesas-container"
                  @click.stop="abrirLancarDespesas(cont)"
                  title="Lançar Despesas"
                >
                  <i class="bi bi-receipt me-1"></i> Despesas
                </button>
                <!-- Botão Visualizar Container -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary fw-bold btn-viewer-container"
                  @click.stop="abrirContainerViewer(cont)"
                  title="Visualizar detalhes do container"
                >
                  <i class="bi bi-eye me-1"></i>
                </button>
              </div>
            </div>

            <!-- Info do Container -->
            <div class="container-info">
              <div class="info-row">
                <span class="info-label">Tara:</span>
                <span class="info-value">{{ formatarNumero(cont.pesoTaraKg) }} kg</span>
              </div>
              <div class="info-row">
                <span class="info-label">VGM:</span>
                <span class="info-value">{{ formatarNumero(cont.vgmKg) }} kg</span>
              </div>
              <div class="info-row">
                <span class="info-label">Bruto Total:</span>
                <span class="info-value fw-bold">{{ formatarNumero(pesoBrutoTotalContainer(cont.idEmbarqueContainer)) }} kg</span>
              </div>
            </div>

            <!-- Itens Alocados -->
            <div class="container-conteudo">
              <div v-if="(alocacoesDoContainer(cont.idEmbarqueContainer) || []).length === 0" class="container-empty">
                <i class="bi bi-download"></i>
                Arraste lotes para cá
              </div>
              <div v-else class="container-itens">
                <div
                  v-for="pl in alocacoesDoContainer(cont.idEmbarqueContainer)"
                  :key="pl.idEmbarquePackingList"
                  class="container-item-alocado"
                >
                  <div class="container-item-content">
                    <i class="bi bi-file-earmark me-1"></i>
                    <span class="container-item-nome">{{ pl.descricao || `Lote #${pl.idEmbarquePackingList}` }}</span>
                  </div>
                  <button
                    v-if="podeEditar"
                    type="button"
                    class="btn btn-sm btn-outline-danger desalocar-container-item-btn"
                    title="Desalocar lote deste container"
                    @click.stop="onDesvincularContainer(pl)"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- MODAL DE FRACIONAMENTO -->
  <!-- ============================================================ -->
  <Teleport to="body" v-if="showFractionModal">
    <div class="modal-overlay" :style="{ zIndex: zFraction }" @click.self="fecharModal">
      <div class="modal-card">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-arrows-split me-2"></i>
            Alocar Item ao Lote
          </h5>
          <button type="button" class="btn-close" @click="fecharModal"></button>
        </div>

        <div class="modal-body">
          <!-- Info de Origem e Destino -->
          <div class="modal-info-box">
            <div class="info-origem-destino">
              <div class="info-item">
                <span class="info-label">Destino:</span>
                <span class="info-valor fw-bold">{{ fractionModalData.packingListDescricao }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Origem:</span>
                <span class="info-valor">{{ fractionModalData.nomeProduto }} <code v-if="fractionModalData.sku">{{ fractionModalData.sku }}</code></span>
              </div>
            </div>
          </div>

          <!-- Lote de Fabricação (visível apenas se o produto controla lote) -->
          <div v-if="fractionModalData.controlaLote" class="mb-3">
            <CampoBusca
              id="idLoteFabricacaoModal"
              label="Lote de Fabricação"
              v-model="fractionModalData.idLoteFabricacao"
              :configuracao-busca="configBuscaLoteFabricacaoModal"
              :disabled="!podeEditar"
            />
          </div>

          <!-- Aviso de conversão -->
          <div v-if="fractionModalData.fatorConversao && Number(fractionModalData.fatorConversao) > 0"
            class="alert alert-info py-2 mb-3"
          >
            <i class="bi bi-info-circle me-2"></i>
            Fator de conversão: {{ fractionModalData.fatorConversao }}. As quantidades são sincronizadas automaticamente.
          </div>
          <div
            v-else-if="fractionModalData.fatorConversao !== null && Number(fractionModalData.fatorConversao) === 0"
            class="alert alert-warning py-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle me-2"></i>
            Fator de conversão zero ou nulo. As quantidades devem ser informadas independentemente.
          </div>

          <!-- Botão Calcular (conversão centralizada no backend) -->
          <div class="d-flex justify-content-end mb-2">
            <button type="button" class="btn btn-sm btn-outline-primary"
              @click="calcularConversao('fornecedor')">
              <i class="bi bi-calculator me-1"></i> Calcular
            </button>
          </div>

          <!-- Sliders de Quantidade -->
          <div class="sliders-stacked">
            <!-- Área Fornecedor -->
            <div class="slider-section">
              <div class="slider-section-header">
                <i class="bi bi-box-seam me-1"></i>
                Qtd. Fornecedor
                <small class="text-muted">({{ getTextoLista(listas.LstUnidadeMedidaFornecedor, fractionModalData.lstUnidadeMedidaFornecedor) }})</small>
              </div>
              <div class="slider-row">
                <input type="range" class="form-range slider-row-main"
                       :min="0" :max="fractionModalData.saldoDisponivelForn" step="0.1"
                       v-model.number="fractionModalData.quantidadeForn" />
                <div class="slider-row-controls">
                  <div class="slider-row-buttons">
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                            title="Aumentar 1 unidade"
                            @click="ajustarQuantidade('quantidadeForn', 1)"
                            :disabled="fractionModalData.quantidadeForn >= fractionModalData.saldoDisponivelForn">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                            title="Aumentar 0.1"
                            @click="ajustarQuantidade('quantidadeForn', 0.1)"
                            :disabled="fractionModalData.quantidadeForn >= fractionModalData.saldoDisponivelForn">
                      <i class="bi bi-plus-lg"></i> 0,1
                    </button>
                  </div>
                  <div class="slider-row-value">
                    <input type="number" class="form-control form-control-sm"
                           :min="0" :max="fractionModalData.saldoDisponivelForn" step="0.01"
                           v-model.number="fractionModalData.quantidadeForn"
                           @blur="calcularConversao('fornecedor')" />
                    <span class="slider-max">/ {{ formatarNumero(fractionModalData.saldoDisponivelForn) }}</span>
                  </div>
                  <div class="slider-row-buttons">
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                            title="Diminuir 1 unidade"
                            @click="ajustarQuantidade('quantidadeForn', -1)"
                            :disabled="fractionModalData.quantidadeForn < 1">
                      <i class="bi bi-dash-lg"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                            title="Diminuir 0.1"
                            @click="ajustarQuantidade('quantidadeForn', -0.1)"
                            :disabled="fractionModalData.quantidadeForn < 0.1">
                      <i class="bi bi-dash-lg"></i> 0,1
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr class="slider-divider" />

            <!-- Área Básica -->
            <div class="slider-section">
              <div class="slider-section-header">
                <i class="bi bi-box me-1"></i>
                Qtd. Básica (Estoque)
                <small class="text-muted">({{ getTextoLista(listas.LstUnidadeMedidaBasica, fractionModalData.lstUnidadeMedidaBasica) }})</small>
              </div>
              <div class="slider-row">
                <input type="range" class="form-range slider-row-main"
                       :min="0" :max="fractionModalData.saldoDisponivelBase" step="0.1"
                       v-model.number="fractionModalData.quantidadeBase" />
                <div class="slider-row-controls">
                  <div class="slider-row-buttons">
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                            title="Aumentar 1 unidade"
                            @click="ajustarQuantidade('quantidadeBase', 1)"
                            :disabled="fractionModalData.quantidadeBase >= fractionModalData.saldoDisponivelBase">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                            title="Aumentar 0.1"
                            @click="ajustarQuantidade('quantidadeBase', 0.1)"
                            :disabled="fractionModalData.quantidadeBase >= fractionModalData.saldoDisponivelBase">
                      <i class="bi bi-plus-lg"></i> 0,1
                    </button>
                  </div>
                  <div class="slider-row-value">
                    <input type="number" class="form-control form-control-sm"
                           :min="0" :max="fractionModalData.saldoDisponivelBase" step="0.01"
                           v-model.number="fractionModalData.quantidadeBase"
                           @blur="calcularConversao('base')" />
                    <span class="slider-max">/ {{ formatarNumero(fractionModalData.saldoDisponivelBase) }}</span>
                  </div>
                  <div class="slider-row-buttons">
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                            title="Diminuir 1 unidade"
                            @click="ajustarQuantidade('quantidadeBase', -1)"
                            :disabled="fractionModalData.quantidadeBase < 1">
                      <i class="bi bi-dash-lg"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                            title="Diminuir 0.1"
                            @click="ajustarQuantidade('quantidadeBase', -0.1)"
                            :disabled="fractionModalData.quantidadeBase < 0.1">
                      <i class="bi bi-dash-lg"></i> 0,1
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ============================================================ -->
          <!-- ALERTA DE PESO (Rateio automático) -->
          <!-- ============================================================ -->

          <hr class="slider-divider" />

          <div class="alert" :class="`alert-${mensagemAlertaPeso.tipo}`" role="alert">
            <div class="d-flex align-items-center gap-2">
              <i class="bi" :class="mensagemAlertaPeso.icone"></i>
              <span>{{ mensagemAlertaPeso.texto }}</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="fecharModal">
            Cancelar
          </button>
          <button type="button" class="btn btn-warning text-dark fw-bold" @click="confirmarAlocacao">
            <i class="bi bi-check-circle me-1"></i>
            Confirmar Alocação
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ============================================================ -->
  <!-- MODAL DE RELATÓRIO DE DISTRIBUIÇÃO -->
  <!-- ============================================================ -->
  <Teleport to="body" v-if="showRelatorioModal">
    <div class="modal-overlay" :style="{ zIndex: zRelatorio }" @click.self="showRelatorioModal = false">
      <div class="modal-card modal-relatorio">
        <div class="modal-header modal-header-relatorio">
          <h5 class="modal-title">
            <i class="bi bi-file-earmark-text me-2"></i>
            Relatório de Distribuição de Cargas
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="showRelatorioModal = false"></button>
        </div>

        <div class="modal-body p-0">
          <!-- Container para PDF -->
          <div ref="relatorioContainerRef" class="relatorio-content">
            <!-- ============================================================ -->
            <!-- HEADER: Identificação do Embarque -->
            <!-- ============================================================ -->
            <div class="relatorio-identificacao">
              <div class="relatorio-identificacao-logo">
                <div class="relatorio-logo-icon">
                  <i class="bi bi-boxes"></i>
                </div>
                <div class="relatorio-logo-texto">
                  <h2 class="relatorio-titulo-principal">Plano de Estufagem</h2>
                  <p class="relatorio-subtitulo">Relatório de Distribuição de Cargas</p>
                </div>
              </div>
              <div class="relatorio-identificacao-dados">
                <div class="id-field">
                  <span class="id-label">Embarque Nº</span>
                  <span class="id-valor">{{ dadosEmbarque.numeroProcessoEmbarque || '---' }}</span>
                </div>
                <div class="id-field">
                  <span class="id-label">Operação</span>
                  <span class="id-valor id-tipo-operacao">{{ getTextoLista(listas.LstTipoOperacaoComex, dadosEmbarque.lstTipoOperacaoComex) || dadosEmbarque.lstTipoOperacaoComex || '---' }}</span>
                </div>
                <div class="id-field">
                  <span class="id-label">Modal</span>
                  <span class="id-valor id-modal">{{ getTextoLista(listas.LstModalTransporte, dadosEmbarque.lstModalTransporte) || dadosEmbarque.lstModalTransporte || '---' }}</span>
                </div>
                <div class="id-field">
                  <span class="id-label">Status</span>
                  <span class="id-valor id-status">{{ getTextoLista(listas.LstStatusEmbarque, dadosEmbarque.lstStatusEmbarque) || dadosEmbarque.lstStatusEmbarque || '---' }}</span>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- INFORMAÇÕES DO TRANSPORTE -->
            <!-- ============================================================ -->
            <div class="relatorio-transporte">
              <div class="transporte-coluna">
                <div class="transporte-item">
                  <i class="bi bi-truck"></i>
                  <div>
                    <span class="transporte-label">Transportador</span>
                    <span class="transporte-valor">{{ dadosEmbarque.nomeTransportador || (dadosEmbarque.idParceiroNegocioTransportador ? parceiros[dadosEmbarque.idParceiroNegocioTransportador] : '---') }}</span>
                  </div>
                </div>
                <div class="transporte-item">
                  <i class="bi bi-geo-alt"></i>
                  <div>
                    <span class="transporte-label">Origem</span>
                    <span class="transporte-valor">{{ getTextoLista(listas.LstPaisOrigem, dadosEmbarque.lstPaisOrigem) || dadosEmbarque.lstPaisOrigem || '---' }} {{ dadosEmbarque.lstPortoOrigem ? '- ' + getTextoLista(listas.LstPortoOrigem, dadosEmbarque.lstPortoOrigem) : '' }}</span>
                  </div>
                </div>
                <div class="transporte-item" v-if="dadosEmbarque.numeroBooking">
                  <i class="bi bi-journal-text"></i>
                  <div>
                    <span class="transporte-label">Booking</span>
                    <span class="transporte-valor">{{ dadosEmbarque.numeroBooking }}</span>
                  </div>
                </div>
              </div>
              <div class="transporte-coluna">
                <div class="transporte-item">
                  <i class="bi bi-calendar-event"></i>
                  <div>
                    <span class="transporte-label">ETD (Previsão Saída)</span>
                    <span class="transporte-valor">{{ dadosEmbarque.dataEmbarqueETD ? new Date(dadosEmbarque.dataEmbarqueETD).toLocaleDateString('pt-BR') : '---' }}</span>
                  </div>
                </div>
                <div class="transporte-item">
                  <i class="bi bi-calendar-check"></i>
                  <div>
                    <span class="transporte-label">ETA (Previsão Chegada)</span>
                    <span class="transporte-valor">{{ dadosEmbarque.dataChegadaETA ? new Date(dadosEmbarque.dataChegadaETA).toLocaleDateString('pt-BR') : '---' }}</span>
                  </div>
                </div>
                <div class="transporte-item">
                  <i class="bi bi-geo-alt"></i>
                  <div>
                    <span class="transporte-label">Destino</span>
                    <span class="transporte-valor">{{ getTextoLista(listas.LstPaisDestino, dadosEmbarque.lstPaisDestino) || dadosEmbarque.lstPaisDestino || '---' }} {{ dadosEmbarque.lstPortoDestino ? '- ' + getTextoLista(listas.LstPortoDestino, dadosEmbarque.lstPortoDestino) : '' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- DOCUMENTOS DE TRANSPORTE -->
            <!-- ============================================================ -->
            <div class="relatorio-documentos" v-if="dadosEmbarque.numeroDocumentoTransporte">
              <div class="transporte-item">
                <i class="bi bi-file-earmark-text"></i>
                <div>
                  <span class="transporte-label">Documento de Transporte</span>
                  <span class="transporte-valor">{{ dadosEmbarque.lstTipoDocumentoTransporte ? getTextoLista(listas.LstTipoDocumentoTransporte, dadosEmbarque.lstTipoDocumentoTransporte) + ' - ' : '' }}{{ dadosEmbarque.numeroDocumentoTransporte }}</span>
                </div>
              </div>
              <div class="transporte-item" v-if="dadosEmbarque.numeroViagem">
                <i class="bi bi-signpost-2"></i>
                <div>
                  <span class="transporte-label">Viagem</span>
                  <span class="transporte-valor">{{ dadosEmbarque.numeroViagem }} {{ dadosEmbarque.nomeVeiculoTransporte ? '- ' + dadosEmbarque.nomeVeiculoTransporte : '' }}</span>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- DATAS E PRAZOS -->
            <!-- ============================================================ -->
            <div class="relatorio-datas-geracao">
              <span>Relatório gerado em {{ new Date().toLocaleString('pt-BR') }}</span>
            </div>

            <!-- ============================================================ -->
            <!-- RESUMO GERAL -->
            <!-- ============================================================ -->
            <div class="relatorio-resumo">
              <div class="resumo-card resumo-container">
                <i class="bi bi-boxes"></i>
                <span class="resumo-numero">{{ containerItems.length }}</span>
                <span class="resumo-rotulo">Containers</span>
              </div>
              <div class="resumo-card resumo-packing">
                <i class="bi bi-list-check"></i>
                <span class="resumo-numero">{{ packingListItems.length }}</span>
                <span class="resumo-rotulo">Packing Lists</span>
              </div>
              <div class="resumo-card resumo-peso">
                <i class="bi bi-speedometer2"></i>
                <span class="resumo-numero">{{ Number(calcularPesoBrutoTotal()).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} kg</span>
                <span class="resumo-rotulo">Peso Bruto Total</span>
              </div>
              <div class="resumo-card resumo-volume">
                <i class="bi bi-box"></i>
                <span class="resumo-numero">{{ Number(calcularVolumeTotal()).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} m³</span>
                <span class="resumo-rotulo">Volume Total</span>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- LISTA DE CONTAINERS -->
            <!-- ============================================================ -->
            <div class="relatorio-containers">
              <div v-for="container in containerItemsOrdenados" :key="container.idEmbarqueContainer" class="relatorio-container-item">
                <!-- Cabeçalho do Container -->
                <div class="container-header-relatorio">
                  <div class="container-titulo">
                    <i class="bi bi-box-seam"></i>
                    Container {{ container.numeroContainer || 'S/ Nº' }}
                    <span v-if="container.lstTipoContainer" class="container-tipo-badge">
                      {{ getTextoLista(listas.LstTipoContainer, container.lstTipoContainer) }}
                    </span>
                    <!-- Badge de Volumes no Relatório -->
                    <span v-if="volumesPorContainer(container.idEmbarqueContainer).length > 0" class="relatorio-volumes-badge">
                      <template v-for="(vol, idx) in volumesPorContainer(container.idEmbarqueContainer)" :key="idx">
                        <i class="bi bi-box me-1"></i>
                        {{ vol.quantidade }} {{ vol.tipoTexto }}
                        <span v-if="idx < volumesPorContainer(container.idEmbarqueContainer).length - 1" class="volumes-sep">|</span>
                      </template>
                    </span>
                  </div>
                  <div class="container-metrics">
                    <div class="metric">
                      <span class="metric-label">Tara</span>
                      <span class="metric-value">{{ formatarNumero(container.pesoTaraKg) }}</span>
                      <span class="metric-unit">kg</span>
                    </div>
                    <div class="metric-divider"></div>
                    <div class="metric">
                      <span class="metric-label">VGM</span>
                      <span class="metric-value">{{ formatarNumero(container.vgmKg) }}</span>
                      <span class="metric-unit">kg</span>
                    </div>
                    <div class="metric-divider"></div>
                    <div class="metric metric-destaque">
                      <span class="metric-label">Bruto</span>
                      <span class="metric-value">{{ formatarNumero(pesoBrutoTotalContainer(container.idEmbarqueContainer)) }}</span>
                      <span class="metric-unit">kg</span>
                    </div>
                  </div>
                </div>

                <!-- Packing Lists do Container -->
                <div class="container-packing-lists">
                  <div v-for="pl in alocacoesDoContainer(container.idEmbarqueContainer)" :key="pl.idEmbarquePackingList" class="relatorio-pl-item">
                    <!-- Cabeçalho do Packing List -->
                    <div class="pl-header-relatorio">
                      <div class="pl-titulo">
                        <i class="bi bi-list-check"></i>
                        {{ pl.descricao || `Lote #${pl.idEmbarquePackingList}` }}
                      </div>
                      <div class="pl-metrics">
                        <span class="pl-metric"><span class="pl-metric-label">Volumes:</span> {{ formatarNumero(pl.quantidadeVolumes, 0) }} {{ getTextoLista(listas.LstTipoVolume, pl.lstTipoVolume) }}</span>
                        <span class="pl-metric-sep">|</span>
                        <span class="pl-metric"><span class="pl-metric-label">Líq:</span> {{ formatarNumero(pl.pesoLiquidoTotalKg) }} kg</span>
                        <span class="pl-metric-sep">|</span>
                        <span class="pl-metric"><span class="pl-metric-label">Bruto:</span> {{ formatarNumero(pl.pesoBrutoTotalKg) }} kg</span>
                        <span class="pl-metric-sep">|</span>
                        <span class="pl-metric"><span class="pl-metric-label">CBM:</span> {{ formatarNumero(pl.volumeCBMTotal) }} m³</span>
                      </div>
                    </div>

                    <!-- Produtos do Packing List -->
                    <table class="relatorio-tabela-produtos rp-table" v-if="pl.produtos && pl.produtos.length > 0">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Produto</th>
                          <th class="th-num">Qtd. Fornecedor</th>
                          <th class="th-num">Qtd. Base</th>
                          <th class="th-num">Peso Líq (kg)</th>
                          <th class="th-num">Peso Bruto (kg)</th>
                          <th class="th-num">Volume (m³)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="prod in pl.produtos" :key="prod.idEmbarquePackingListProduto">
                          <td class="td-sku"><span class="sku-code">{{ prod.sku || '-' }}</span></td>
                          <td class="td-produto">{{ prod.descricaoProduto || 'Produto #' + prod.idProduto }}</td>
                          <td class="td-num">
                            {{ Number(prod.quantidadeEmbarcadaUnFornecedor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) }}
                            <span class="td-unidade">{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, prod.lstUnidadeMedidaFornecedor) }}</span>
                          </td>
                          <td class="td-num">
                            {{ Number(prod.quantidadeEmbarcadaUnBasica || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) }}
                            <span class="td-unidade">{{ getTextoLista(listas.LstUnidadeMedidaBasica, prod.lstUnidadeMedidaBasica) }}</span>
                          </td>
                          <td class="td-num">{{ (prod.pesoLiquidoKg || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) }}</td>
                          <td class="td-num">{{ (prod.pesoBrutoKg || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) }}</td>
                          <td class="td-num">{{ (prod.volumeCBM || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) }}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colspan="2" class="td-footer-label">Totais do Packing List</td>
                          <td class="td-num td-footer">{{ Number(somarQtdForn(pl.produtos)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) }}</td>
                          <td class="td-num td-footer">{{ Number(somarQtdBase(pl.produtos)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) }}</td>
                          <td class="td-num td-footer">{{ (somarPesoLiquido(pl.produtos) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) }}</td>
                          <td class="td-num td-footer">{{ (somarPesoBruto(pl.produtos) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) }}</td>
                          <td class="td-num td-footer">{{ (somarVolume(pl.produtos) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) }}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <div v-else class="pl-sem-produtos">
                      <i class="bi bi-exclamation-triangle"></i> Nenhum produto vinculado
                    </div>
                  </div>

                  <!-- Mensagem se container vazio -->
                  <div v-if="!alocacoesDoContainer(container.idEmbarqueContainer).length" class="container-vazio">
                    <i class="bi bi-inbox"></i> Container sem packing lists alocados
                  </div>
                </div>
              </div>
            </div>

            <!-- ============================================================ -->
            <!-- PACKING LISTS NÃO ALOCADOS -->
            <!-- ============================================================ -->
            <div v-if="packingListNaoAlocados.length > 0" class="relatorio-nao-alocados">
              <div class="nao-alocados-header">
                <i class="bi bi-exclamation-circle"></i>
                Packing Lists Pendentes de Alocação
                <span class="nao-alocados-count">{{ packingListNaoAlocados.length }}</span>
              </div>
              <div v-for="pl in packingListNaoAlocados" :key="pl.idEmbarquePackingList" class="relatorio-pl-item nao-alocado-item">
                <div class="pl-header-relatorio">
                  <div class="pl-titulo">
                    <i class="bi bi-list-check"></i>
                    {{ pl.descricao || `Lote #${pl.idEmbarquePackingList}` }}
                  </div>
                  <div class="pl-metrics">
                    <span class="pl-metric"><span class="pl-metric-label">Volumes:</span> {{ formatarNumero(pl.quantidadeVolumes, 0) }}</span>
                    <span class="pl-metric-sep">|</span>
                    <span class="pl-metric"><span class="pl-metric-label">Peso:</span> {{ formatarNumero(pl.pesoBrutoTotalKg) }} kg</span>
                    <span class="pl-metric-sep">|</span>
                    <span class="pl-metric"><span class="pl-metric-label">CBM:</span> {{ formatarNumero(pl.volumeCBMTotal) }} m³</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer modal-footer-relatorio">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="showRelatorioModal = false">
            <i class="bi bi-x-lg me-1"></i> Fechar
          </button>
          <button type="button" class="btn btn-sm btn-primary" @click="gerarPDF">
            <i class="bi bi-file-earmark-pdf me-1"></i> Gerar PDF
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ============================================================ -->
  <!-- MODAL: Lançar Despesas por Container -->
  <!-- ============================================================ -->
  <ModalFormularioV2
    v-model="showModalDocumentosGrid"
    titulo="Lançar Despesas"
    tamanho="xxl"
    @update:model-value="showModalDocumentosGrid = $event"
  >
    <DocumentoEntradaGrid
      ref="documentoEntradaGridRef"
      :id-embarque-container="containerEmFoco"
      v-model:documento-selecionado="documentoSelecionado"
      :tem-modulo-documento="true"
      :tem-completo="true"
      :tem-visualizacao="true"
      :pode-editar="podeEditar"
      :listas="listas"
      :exibir-primeiro-produto="true"
      :id-organizacao="authStore.empresaAtiva"
      :com-smart-mode="true"
      contexto-origem="embarque"
      modo-alocacao="livre"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <template #footer>
      <div class="d-flex gap-2 w-100 justify-content-end">
        <button type="button" class="btn btn-outline-secondary" @click="showModalDocumentosGrid = false">
          Fechar
        </button>
      </div>
    </template>
  </ModalFormularioV2>

  <!-- ============================================================ -->
  <!-- MODAL: Visualização do Container -->
  <!-- ============================================================ -->
  <ModalFormularioV2
    v-model="showContainerViewerModal"
    titulo="Container"
    tamanho="xl"
    :cor-header="'var(--bs-primary-bg-subtle)'"
    @update:model-value="showContainerViewerModal = $event"
  >
    <div v-if="containerViewerData" class="container-viewer-content">
      <!-- Dados do Container -->
      <div class="viewer-section">
        <h6 class="viewer-section-title"><i class="bi bi-box-seam me-2"></i>Dados do Container</h6>
        <div class="viewer-grid">
          <div class="viewer-field">
            <span class="viewer-label">Número</span>
            <span class="viewer-value">{{ containerViewerData.numeroContainer || 'S/ Nº' }}</span>
          </div>
          <div class="viewer-field">
            <span class="viewer-label">Tipo</span>
            <span class="viewer-value">{{ getTextoLista(listas.LstTipoContainer, containerViewerData.lstTipoContainer) || '-' }}</span>
          </div>
          <div class="viewer-field">
            <span class="viewer-label">Tara</span>
            <span class="viewer-value">{{ formatarNumero(containerViewerData.pesoTaraKg) }} kg</span>
          </div>
          <div class="viewer-field">
            <span class="viewer-label">VGM</span>
            <span class="viewer-value">{{ formatarNumero(containerViewerData.vgmKg) }} kg</span>
          </div>
          <div class="viewer-field">
            <span class="viewer-label">Bruto Total</span>
            <span class="viewer-value fw-bold">{{ formatarNumero(pesoBrutoTotalContainer(containerViewerData.idEmbarqueContainer)) }} kg</span>
          </div>
        </div>
      </div>

      <!-- Packing Lists Alocados -->
      <div class="viewer-section">
        <h6 class="viewer-section-title"><i class="bi bi-list-check me-2"></i>Packing Lists Alocados</h6>
        <template v-if="(alocacoesDoContainer(containerViewerData.idEmbarqueContainer) || []).length === 0">
          <div class="viewer-empty">
            <i class="bi bi-inbox me-2"></i> Nenhum packing list alocado neste container.
          </div>
        </template>
        <template v-else>
          <div v-for="pl in alocacoesDoContainer(containerViewerData.idEmbarqueContainer)" :key="pl.idEmbarquePackingList" class="viewer-pl-card">
            <div class="viewer-pl-header">
              <span class="viewer-pl-nome">{{ pl.descricao || `Lote #${pl.idEmbarquePackingList}` }}</span>
              <span class="viewer-pl-volumes">{{ formatarNumero(pl.quantidadeVolumes, 0) }} {{ getTextoLista(listas.LstTipoVolume, pl.lstTipoVolume) }}</span>
            </div>
            <div class="viewer-pl-metrics">
              <span>Peso Líq: {{ formatarNumero(pl.pesoLiquidoTotalKg) }} kg</span>
              <span class="viewer-metric-sep">|</span>
              <span>Peso Bruto: {{ formatarNumero(pl.pesoBrutoTotalKg) }} kg</span>
              <span class="viewer-metric-sep">|</span>
              <span>Volume: {{ formatarNumero(pl.volumeCBMTotal) }} m³</span>
            </div>
            <div class="viewer-pl-produtos">
              <div class="viewer-pl-produtos-header">
                <i class="bi bi-box-seam me-1"></i> Produtos Vinculados
                <span class="viewer-pl-produtos-count">{{ (pl.produtos || []).length }} itens</span>
              </div>
              <div v-if="pl.produtos && pl.produtos.length > 0" class="viewer-pl-com-produtos">
                <div v-for="prod in pl.produtos" :key="prod.idEmbarquePackingListProduto" class="viewer-produto-item">
                  <div class="viewer-produto-info">
                    <code v-if="prod.sku" class="viewer-produto-sku">{{ prod.sku }}</code>
                    <span class="viewer-produto-nome">{{ (prod.sku && prod.descricaoProduto ? ' - ' : '') }}{{ prod.descricaoProduto || 'Produto #' + prod.idProduto }}</span>
                  </div>
                  <div class="viewer-produto-qtd">
                    <span class="viewer-produto-qtd-label">Forn:</span>
                    <span>{{ formatarNumero(prod.quantidadeEmbarcadaUnFornecedor) }}</span>
                    <span class="viewer-produto-un">{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, prod.lstUnidadeMedidaFornecedor) }}</span>
                    <span class="viewer-produto-sep">|</span>
                    <span class="viewer-produto-qtd-label">Base:</span>
                    <span>{{ formatarNumero(prod.quantidadeEmbarcadaUnBasica) }}</span>
                    <span class="viewer-produto-un">{{ getTextoLista(listas.LstUnidadeMedidaBasica, prod.lstUnidadeMedidaBasica) }}</span>
                    <template v-if="prod.pesoBrutoKg || prod.pesoLiquidoKg">
                      <span class="viewer-produto-sep">|</span>
                      <span class="viewer-produto-qtd-label">Peso:</span>
                      <span>{{ formatarNumero(prod.pesoBrutoKg, 2) }}/{{ formatarNumero(prod.pesoLiquidoKg, 2) }} kg</span>
                    </template>
                    <template v-if="prod.volumeCBM">
                      <span class="viewer-produto-sep">|</span>
                      <span class="viewer-produto-qtd-label">Vol:</span>
                      <span>{{ formatarNumero(prod.volumeCBM, 3) }} m³</span>
                    </template>
                  </div>
                </div>
              </div>
              <div v-else class="viewer-pl-sem-produtos">
                <i class="bi bi-exclamation-triangle me-1"></i> Nenhum produto vinculado a este packing list.
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <div class="d-flex gap-2 w-100 justify-content-end">
        <button type="button" class="btn btn-outline-secondary" @click="showContainerViewerModal = false">
          Fechar
        </button>
      </div>
    </template>
  </ModalFormularioV2>
</template>

<style scoped>
/* ============================================================ */
/* GRID PRINCIPAL - 3 COLUNAS */
/* ============================================================ */
.smart-mode-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  min-height: 500px;
}

.smart-column {
  display: flex;
  flex-direction: column;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  overflow: hidden;
}

.column-header {
  padding: 12px 16px;
  background: var(--bs-warning-bg-subtle);
  border-bottom: 1px solid var(--bs-warning-border-subtle);
}

/* Header da coluna Lotes a Estufar - cor danger derivada (mesma intensidade da primeira coluna) */
.column-header-packing {
  background: var(--bs-danger-bg-subtle);
  border-bottom: 1px solid var(--bs-danger-border-subtle);
}

/* Header da coluna Lotes a Estufar quando todos estão alocados - cor success (verde) */
.column-header-packing-completo {
  background: var(--bs-success-bg-subtle);
  border-bottom: 1px solid var(--bs-success-border-subtle);
}

/* Header da coluna Plano de Estufagem - cor primary derivada */
.column-header-containers {
  background: var(--bs-primary-bg-subtle);
  border-bottom: 1px solid var(--bs-primary-border-subtle);
}

.column-header h5 {
  font-size: 0.9rem;
  font-weight: 700;
}

.column-header small {
  font-size: 0.7rem;
}

.column-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  max-height: 600px;
}

/* ============================================================ */
/* EMPTY STATE */
/* ============================================================ */
.empty-state {
  text-align: center;
  padding: 40px 16px;
  color: var(--bs-secondary-color);
}

.empty-state p {
  font-size: 0.85rem;
}

/* ============================================================ */
/* GRUPO DE FORNECEDOR (Coluna Esquerda) */
/* ============================================================ */
.grupo-documento-label {
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-warning-text-emphasis);
  background: var(--bs-warning-bg-subtle);
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.grupo-documento-label:hover {
  background: var(--bs-warning-border-subtle);
}

.grupo-documento-body {
  margin-bottom: 12px;
}

.card-lote-info {
  font-size: 0.72rem;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--bs-warning-text-emphasis);
  background: var(--bs-warning-bg-subtle);
  padding: 2px 6px;
  border-radius: 4px;
}

/* ============================================================ */
/* CARDS DRAGGABLE */
/* ============================================================ */
.draggable-card {
  cursor: grab;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-warning);
  border-radius: 6px;
  background: var(--bs-body-bg);
  transition: transform 0.2s, box-shadow 0.2s;
}

.draggable-card:active {
  cursor: grabbing;
  transform: scale(0.98);
}

.draggable-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.card-title-ai {
  color: var(--bs-info-text-emphasis);
}

/* Mesmas fontes do EmbarquesProdutosSmartMode.vue */
.card-sku {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--bs-danger);
  margin-bottom: 2px;
  line-height: 1.2;
}

.card-sku code {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--bs-danger);
  background: transparent;
  border: none;
  padding: 0;
}

.card-produto-nome {
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--bs-body-color);
  margin-bottom: 6px;
}

.card-fornecedor {
  font-size: 0.72rem;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
  padding: 2px 6px;
  border-radius: 4px;
}

.card-fornecedor .fornecedor-id {
  font-weight: 700;
  font-family: 'Consolas', 'Courier New', monospace;
  color: var(--bs-body-color);
}

.card-fornecedor .fornecedor-nome {
  color: var(--bs-body-color);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-saldo {
  font-size: 0.75rem;
}

.saldo-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.saldo-label {
  color: var(--bs-secondary-color);
}

.saldo-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
}

/* ============================================================ */
/* DROPZONE CARDS */
/* ============================================================ */
.dropzone-card {
  transition: all 0.3s ease;
  border: 2px dashed transparent;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  position: relative;
}

.dropzone-card.drag-over {
  border-color: var(--bs-warning);
  background-color: var(--bs-warning-bg-subtle);
  transform: scale(1.02);
}

/* ============================================================ */
/* CARD LOTE (Coluna Central) */
/* ============================================================ */
.card-lote {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-warning);
  cursor: grab;
}

.card-lote:active {
  cursor: grabbing;
}

.card-lote-pendente {
  border-left-color: var(--bs-danger);
}

.card-lote-alocado {
  border-left-color: var(--bs-success);
  cursor: not-allowed;
}

.lote-badge-pendente {
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--bs-danger-text-emphasis);
  background: var(--bs-danger-bg-subtle);
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 6px;
}

.lote-badge-alocado {
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--bs-success-text-emphasis);
  background: var(--bs-success-bg-subtle);
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 6px;
}

.card-subtitle {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  margin-bottom: 8px;
}

.lote-info {
  font-size: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 1px 0;
}

.info-label {
  color: var(--bs-secondary-color);
}

.info-value {
  font-weight: 600;
}

.lote-flag-ia {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.6rem;
  font-weight: 600;
  color: var(--bs-warning-text-emphasis);
  background: var(--bs-warning-bg-subtle);
  padding: 2px 6px;
  border-radius: 4px;
}

/* ============================================================ */
/* FLAG LOTE PREENCHIDO (Checkbox) */
/* ============================================================ */
/* ============================================================ */
/* ÁREA DE AÇÃO PRINCIPAL (Flag Preenchido / Desalocar Container) */
/* ============================================================ */
.lote-acao-principal {
  margin-bottom: 8px;
}

.lote-acao-principal .lote-flag-checkbox {
  padding: 4px 8px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 6px;
  font-size: 0.75rem;
}

.lote-acao-principal .lote-flag-checkbox.disabled {
  opacity: 0.6;
}

.lote-acao-principal .lote-flag-checkbox .form-check {
  min-height: auto;
  padding-left: 2.2em;
  margin-bottom: 0;
}

.lote-acao-principal .lote-flag-checkbox .form-check-input {
  cursor: pointer;
  margin-top: 0.15em;
}

.lote-acao-principal .lote-flag-checkbox .form-check-input:disabled {
  cursor: not-allowed;
}

.lote-acao-principal .lote-flag-checkbox .form-check-label {
  cursor: pointer;
  font-weight: 500;
}

.lote-acao-principal .lote-flag-checkbox .form-check-input:disabled + .form-check-label {
  cursor: not-allowed;
}

.flag-text {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.flag-text.flag-ok {
  color: var(--bs-success-text-emphasis);
}

.flag-text.flag-pendente {
  color: var(--bs-danger-text-emphasis);
}

/* Botão Desalocar no lugar do checkbox */
.lote-acao-principal .desalocar-btn {
  font-size: 0.7rem;
  padding: 4px 8px;
  width: 100%;
  border-width: 1px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
}

/* ============================================================ */
/* PRODUTOS VINCULADOS AO LOTE */
/* ============================================================ */
.lote-produtos {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--bs-border-color-translucent);
}

.produtos-header {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--bs-secondary-color);
  margin-bottom: 4px;
}

.produto-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  padding: 4px 6px;
  margin-bottom: 3px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  font-size: 0.7rem;
}

.produto-info {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 0.7rem;
  white-space: nowrap;
}

.produto-tag-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.tag-label {
  font-weight: 600;
  color: var(--bs-secondary-color);
  font-size: 0.7rem;
}

.tag-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  color: var(--bs-body-color);
  font-size: 0.7rem;
}

.tag-unidade {
  font-weight: 400;
  color: var(--bs-secondary-color);
  font-size: 0.7rem;
}

.tag-separator {
  color: var(--bs-border-color);
  font-weight: 300;
  font-size: 0.7rem;
  margin: 0 2px;
}

/* Estilos legados mantidos para compatibilidade */
.produto-nome {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.produto-sku {
  font-size: 0.65rem;
  flex-shrink: 0;
}

.produto-qtd {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 4px;
}

.produto-qtd small {
  font-weight: 400;
  color: var(--bs-secondary-color);
}

.desalocar-produto-btn {
  padding: 0 4px;
  font-size: 0.6rem;
  line-height: 1;
  flex-shrink: 0;
  border-width: 1px;
}

/* ============================================================ */
/* CARD CONTAINER (Coluna Direita) */
/* ============================================================ */
.card-container {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-top: 3px solid var(--bs-primary);
  min-height: 180px;
}

.container-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.container-numero {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--bs-primary-text-emphasis);
  font-family: 'Consolas', 'Courier New', monospace;
}

.container-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.container-header-right .lote-flag-ia {
  position: static;
}

.btn-lancar-despesas-container {
  font-size: 0.6rem;
  padding: 1px 6px;
  line-height: 1.4;
  border-width: 1px;
  white-space: nowrap;
}

.container-tipo {
  font-size: 0.7rem;
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
  padding: 2px 8px;
  border-radius: 4px;
}

.container-info {
  font-size: 0.75rem;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.container-conteudo {
  flex: 1;
}

.container-empty {
  text-align: center;
  padding: 20px;
  color: var(--bs-secondary-color);
  font-size: 0.75rem;
  font-style: italic;
  border: 1px dashed var(--bs-border-color);
  border-radius: 4px;
}

.container-itens {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.container-item-alocado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 0.75rem;
  padding: 4px 8px;
  background: var(--bs-primary-bg-subtle);
  border: 1px solid var(--bs-primary-border-subtle);
  border-radius: 4px;
  color: var(--bs-primary-text-emphasis);
}

.container-item-content {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.container-item-nome {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desalocar-container-item-btn {
  padding: 0 3px;
  font-size: 0.55rem;
  line-height: 1;
  flex-shrink: 0;
  border-width: 1px;
}

/* ============================================================ */
/* MODAL DE FRACIONAMENTO */
/* ============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.modal-card {
  background: var(--bs-body-bg);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  width: 100%;
  max-width: 560px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--bs-warning);
  color: #000;
  border-bottom: 1px solid var(--bs-border-color);
}

.modal-header .btn-close {
  filter: invert(0);
}

.modal-title {
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
  background: var(--bs-tertiary-bg);
}

.modal-info-box {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-origem-destino {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-item {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
}

.info-item .info-label {
  font-weight: 600;
  min-width: 70px;
  color: var(--bs-secondary-color);
}

.info-item .info-valor {
  color: var(--bs-body-color);
}

/* Slider Group */
.slider-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slider-values {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-values input[type="number"] {
  width: 120px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-weight: 600;
}

.slider-max {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  font-family: 'Consolas', 'Courier New', monospace;
}

/* ============================================================ */
/* LAYOUT EMPILHADO DOS SLIDERS */
/* ============================================================ */
.sliders-stacked {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slider-section {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
}

.slider-section-header {
  font-weight: 700;
  font-size: 0.8rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.slider-section-header small {
  font-weight: 400;
  font-size: 0.7rem;
}

.slider-divider {
  margin: 0;
  border: 0;
  border-top: 2px dashed var(--bs-border-color);
  opacity: 0.5;
}

/* ============================================================ */
/* CONTROLES DO SLIDER (botões + input) */
/* ============================================================ */
.slider-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.slider-buttons-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-buttons-right {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-value-box {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.slider-btn {
  font-size: 0.6rem;
  padding: 2px 8px;
  line-height: 1.3;
  border-width: 1px;
  white-space: nowrap;
  min-width: 36px;
}

.slider-btn-decimal {
  font-size: 0.55rem;
  opacity: 0.8;
  min-width: 32px;
}

/* ============================================================ */
/* LAYOUT SLIDER-ROW (peso/volume - controles à direita) */
/* ============================================================ */
.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-row-main {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.slider-row-controls {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
}

.slider-row-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-row-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 110px;
}

.slider-row-value .form-control-sm {
  width: 100%;
  font-family: 'Consolas', 'Courier New', monospace;
  font-weight: 600;
  text-align: center;
}

.slider-row-value .slider-max {
  font-size: 0.65rem;
  color: var(--bs-secondary-color);
  font-family: 'Consolas', 'Courier New', monospace;
  white-space: nowrap;
}

/* ============================================================ */
/* RESPONSIVIDADE */
/* ============================================================ */
@media (max-width: 1200px) {
  .smart-mode-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .smart-mode-grid {
    grid-template-columns: 1fr;
  }
}

/* ============================================================ */
/* MODAL DE RELATÓRIO - Design Elegante & Profissional */
/* ============================================================ */
.modal-relatorio {
  max-width: 95vw;
  width: 1200px;
  max-height: 90vh;
}

.modal-relatorio .modal-body {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

/* Header do modal */
.modal-header-relatorio {
  background: linear-gradient(135deg, #0a1628 0%, #1a2a4a 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding: 16px 24px;
}

.modal-header-relatorio .modal-title {
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.02em;
}

/* Footer do modal */
.modal-footer-relatorio {
  padding: 12px 24px;
  background: #f8f9fc;
  border-top: 1px solid #e8ecf1;
}

/* ============================================================ */
/* CONTEÚDO DO RELATÓRIO */
/* ============================================================ */
.relatorio-content {
  padding: 30px;
  background: #ffffff;
  color: #1a1a2e;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.5;
}

/* ============================================================ */
/* IDENTIFICAÇÃO DO EMBARQUE (Topo) */
/* ============================================================ */
.relatorio-identificacao {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 24px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e8ecf1;
}

.relatorio-identificacao-logo {
  display: flex;
  align-items: center;
  gap: 16px;
}

.relatorio-logo-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0a1628 0%, #1a2a4a 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: #ffffff;
}

.relatorio-logo-texto {
  display: flex;
  flex-direction: column;
}

.relatorio-titulo-principal {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: #0a1628;
  line-height: 1.2;
}

.relatorio-subtitulo {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 2px 0 0 0;
  font-weight: 400;
}

.relatorio-identificacao-dados {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.id-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 100px;
}

.id-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  font-weight: 600;
}

.id-valor {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0a1628;
}

.id-tipo-operacao {
  color: #2563eb;
}

.id-modal {
  color: #7c3aed;
}

.id-status {
  color: #059669;
}

/* ============================================================ */
/* INFORMAÇÕES DO TRANSPORTE */
/* ============================================================ */
.relatorio-transporte {
  display: flex;
  gap: 32px;
  padding: 16px 20px;
  background: #f8f9fc;
  border: 1px solid #e8ecf1;
  border-radius: 10px;
  margin-bottom: 16px;
}

.transporte-coluna {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.transporte-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.transporte-item i {
  width: 20px;
  text-align: center;
  font-size: 0.95rem;
  color: #6b7280;
}

.transporte-item > div {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.transporte-label {
  font-size: 0.7rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
}

.transporte-valor {
  font-size: 0.82rem;
  color: #1f2937;
  font-weight: 500;
}

/* Documentos de Transporte */
.relatorio-documentos {
  display: flex;
  gap: 32px;
  padding: 12px 20px;
  background: #f0f4ff;
  border: 1px solid #dbe4f0;
  border-radius: 10px;
  margin-bottom: 16px;
}

/* Data de geração */
.relatorio-datas-geracao {
  display: flex;
  justify-content: flex-end;
  font-size: 0.72rem;
  color: #9ca3af;
  margin-bottom: 24px;
  padding-top: 4px;
}

/* ============================================================ */
/* RESUMO GERAL (Cards) */
/* ============================================================ */
.relatorio-resumo {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.resumo-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e8ecf1;
  transition: box-shadow 0.2s;
}

.resumo-card i {
  font-size: 1.3rem;
  margin-bottom: 6px;
}

.resumo-numero {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0a1628;
  line-height: 1.2;
}

.resumo-rotulo {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #9ca3af;
  letter-spacing: 0.04em;
  font-weight: 600;
  margin-top: 2px;
}

.resumo-container i { color: #2563eb; }
.resumo-packing i { color: #7c3aed; }
.resumo-peso i { color: #059669; }
.resumo-volume i { color: #d97706; }

/* ============================================================ */
/* LISTA DE CONTAINERS */
/* ============================================================ */
.relatorio-containers {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.relatorio-container-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.container-header-relatorio {
  background: linear-gradient(135deg, #f8f9fc 0%, #f0f2f7 100%);
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
}

.container-titulo {
  font-weight: 700;
  font-size: 0.95rem;
  color: #0a1628;
  display: flex;
  align-items: center;
  gap: 8px;
}

.container-titulo i {
  color: #2563eb;
  font-size: 1.05rem;
}

.container-tipo-badge {
  display: inline-block;
  background: #e0e7ff;
  color: #1e40af;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.container-metrics {
  display: flex;
  align-items: center;
  gap: 12px;
}

.metric {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.metric-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.metric-value {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2937;
  font-family: 'JetBrains Mono', 'Consolas', monospace;
}

.metric-unit {
  font-size: 0.7rem;
  color: #9ca3af;
}

.metric-destaque .metric-value {
  color: #059669;
}

.metric-divider {
  width: 1px;
  height: 24px;
  background: #d1d5db;
}

/* ============================================================ */
/* PACKING LISTS DENTRO DO CONTAINER */
/* ============================================================ */
.container-packing-lists {
  padding: 12px;
  background: #ffffff;
}

.relatorio-pl-item {
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.pl-header-relatorio {
  background: #f9fafb;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
}

.pl-titulo {
  font-weight: 600;
  font-size: 0.85rem;
  color: #1f2937;
}

.pl-titulo i {
  color: #f59e0b;
  margin-right: 6px;
}

.pl-metrics {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: #6b7280;
}

.pl-metric-label {
  color: #9ca3af;
  font-weight: 500;
}

.pl-metric-sep {
  color: #d1d5db;
  font-weight: 300;
}

/* ============================================================ */
/* TABELA DE PRODUTOS */
/* ============================================================ */
.relatorio-tabela-produtos {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.relatorio-tabela-produtos thead tr {
  background: #f3f4f6;
}

.relatorio-tabela-produtos th {
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #d1d5db;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.relatorio-tabela-produtos th.th-num {
  text-align: right;
}

.relatorio-tabela-produtos td {
  padding: 7px 10px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  color: #1f2937;
}

.relatorio-tabela-produtos tbody tr:nth-child(even) {
  background: #fafbfc;
}

.relatorio-tabela-produtos tbody tr:hover {
  background: #f0f4ff;
}

.relatorio-tabela-produtos tfoot td {
  background: #f3f4f6;
  font-weight: 700;
  color: #1f2937;
  border-top: 2px solid #d1d5db;
  padding: 8px 10px;
}

.td-sku {
  width: 110px;
}

.sku-code {
  font-family: 'JetBrains Mono', 'Consolas', monospace;
  font-weight: 600;
  font-size: 0.76rem;
  color: #dc2626;
  background: #fef2f2;
  padding: 1px 6px;
  border-radius: 4px;
}

.td-produto {
  min-width: 180px;
  font-weight: 500;
}
.td-num {
  text-align: right;
  white-space: nowrap;
  font-family: 'JetBrains Mono', 'Consolas', monospace;
  font-weight: 500;
  font-size: 0.76rem;
}

.td-unidade {
  color: #9ca3af;
  font-weight: 400;
  font-size: 0.7rem;
  margin-left: 2px;
}

.td-footer-label {
  text-align: right;
  font-weight: 700;
  font-size: 0.78rem;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.td-footer {
  font-weight: 700;
  color: #0a1628;
  border-top: 2px solid #9ca3af;
}

/* Sem produtos */
.pl-sem-produtos {
  padding: 16px;
  text-align: center;
  color: #dc2626;
  font-size: 0.82rem;
  background: #fef2f2;
}

/* Container vazio */
.container-vazio {
  padding: 24px;
  text-align: center;
  color: #9ca3af;
  font-style: italic;
  font-size: 0.85rem;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
}

/* ============================================================ */
/* PACKING LISTS NÃO ALOCADOS */
/* ============================================================ */
.relatorio-nao-alocados {
  margin-top: 24px;
  border: 1px solid #fca5a5;
  border-radius: 10px;
  padding: 16px;
  background: #fef2f2;
}

.nao-alocados-header {
  font-weight: 700;
  color: #b91c1c;
  margin-bottom: 12px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nao-alocados-header i {
  font-size: 1rem;
}

.nao-alocados-count {
  background: #b91c1c;
  color: #ffffff;
  padding: 1px 8px;
  border-radius: 20px;
  font-size: 0.75rem;
  margin-left: 4px;
}

.nao-alocado-item {
  border-color: #fca5a5;
  background: #ffffff;
}

/* ============================================================ */
/* PRINT STYLES PARA PDF */
/* ============================================================ */
@media print {
  .relatorio-content {
    padding: 15px;
  }

  .relatorio-container-item {
    page-break-inside: avoid;
    margin-bottom: 16px;
  }

  .relatorio-pl-item {
    page-break-inside: avoid;
  }

  .relatorio-tabela-produtos {
  }

  .relatorio-identificacao {
    page-break-inside: avoid;
  }

  .relatorio-transporte {
    page-break-inside: avoid;
  }

  .relatorio-resumo {
    page-break-inside: avoid;
  }
}

/* ============================================================ */
/* BADGE DE VOLUMES NO CONTAINER */
/* ============================================================ */
.container-volumes-badge {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--bs-primary-text-emphasis);
  background: var(--bs-primary-bg-subtle);
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.volumes-sep {
  color: var(--bs-primary-border-subtle);
  margin: 0 2px;
}

/* ============================================================ */
/* BOTAO VIEWER DO CONTAINER */
/* ============================================================ */
.btn-viewer-container {
  font-size: 0.6rem;
  padding: 1px 6px;
  line-height: 1.4;
  border-width: 1px;
  white-space: nowrap;
}

/* ============================================================ */
/* MODAL VIEWER DO CONTAINER */
/* ============================================================ */
.container-viewer-content {
  font-size: 0.85rem;
}

.viewer-section {
  margin-bottom: 20px;
}

.viewer-section:last-child {
  margin-bottom: 0;
}

.viewer-section-title {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--bs-primary-text-emphasis);
  padding-bottom: 8px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--bs-primary-border-subtle);
  display: flex;
  align-items: center;
}

.viewer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.viewer-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.viewer-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color);
  font-weight: 600;
}

.viewer-value {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--bs-body-color);
}

.viewer-empty {
  text-align: center;
  padding: 24px;
  color: var(--bs-secondary-color);
  font-style: italic;
  background: var(--bs-tertiary-bg);
  border: 1px dashed var(--bs-border-color);
  border-radius: 6px;
}

.viewer-pl-card {
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  background: var(--bs-tertiary-bg);
}

.viewer-pl-card:last-child {
  margin-bottom: 0;
}

.viewer-pl-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.viewer-pl-nome {
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--bs-body-color);
}

.viewer-pl-volumes {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bs-primary-text-emphasis);
  background: var(--bs-primary-bg-subtle);
  padding: 2px 8px;
  border-radius: 10px;
}

.viewer-pl-metrics {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.viewer-metric-sep {
  color: var(--bs-border-color);
}

.viewer-pl-produtos {
  border-top: 1px solid var(--bs-border-color-translucent);
  padding-top: 8px;
}

.viewer-produto-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 0.75rem;
}

.viewer-produto-item + .viewer-produto-item {
  border-top: 1px solid var(--bs-border-color-translucent);
}

.viewer-produto-info {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.viewer-produto-sku {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--bs-danger);
  background: transparent;
  border: none;
  padding: 0;
}

.viewer-produto-nome {
  color: var(--bs-body-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer-produto-qtd {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.viewer-produto-sep {
  color: var(--bs-border-color);
  font-weight: 300;
}

.viewer-pl-produtos-header {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--bs-primary-text-emphasis);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.viewer-pl-produtos-count {
  font-size: 0.65rem;
  font-weight: 400;
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
  padding: 1px 6px;
  border-radius: 8px;
}

.viewer-pl-sem-produtos {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  font-style: italic;
  padding: 8px;
  text-align: center;
  background: var(--bs-tertiary-bg);
  border: 1px dashed var(--bs-border-color);
  border-radius: 4px;
}

/* ============================================================ */
/* MODAL VIEWER - HEADER COM COR DA COLUNA PLANO DE ESTUFAGEM */
/* ============================================================ */
:deep(.modal-header-v2) {
  color: var(--bs-body-color);
  border-bottom: 1px solid var(--bs-primary-border-subtle);
}

:deep(.modal-header-v2 .modal-title-v2) {
  color: var(--bs-body-color);
}

:deep(.modal-header-v2 .btn-close) {
  filter: none;
  opacity: 0.65;
}

.viewer-produto-qtd-label {
  font-weight: 400;
  color: var(--bs-secondary-color);
}

.viewer-produto-un {
  font-weight: 400;
  color: var(--bs-secondary-color);
  font-size: 0.65rem;
}

/* ============================================================ */
/* BADGE DE VOLUMES NO RELATORIO */
/* ============================================================ */
.relatorio-volumes-badge {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--bs-primary-text-emphasis);
  background: var(--bs-primary-bg-subtle);
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
}
</style>
